import { useState, useEffect, Component } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import LoginPage from "@/pages/LoginPage";
import { getMe } from "@/lib/api";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-lg p-6 max-w-md w-full text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-red-100 text-red-600 mx-auto flex items-center justify-center text-xl font-bold">
              !
            </div>
            <h2 className="text-lg font-bold text-slate-900">Se produjo un error visual</h2>
            <p className="text-sm text-slate-500">
              {this.state.error?.message || "Ocurrió un detalle al renderizar los datos."}
            </p>
            <div className="flex gap-2 justify-center pt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-slate-800"
              >
                Recargar página
              </button>
              <button
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-100"
              >
                Limpiar sesión
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function MainApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkAuth = async () => {
    const token = localStorage.getItem("viajacrm_token");
    if (!token) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const u = await getMe();
      setUser(u);
    } catch {
      localStorage.removeItem("viajacrm_token");
      localStorage.removeItem("viajacrm_user");
      setUser(null);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    document.title = "ViajaCRM — Clientes y Reservas";
    checkAuth();

    const handleLogout = () => setUser(null);
    window.addEventListener("viajacrm_logout", handleLogout);
    return () => window.removeEventListener("viajacrm_logout", handleLogout);
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-slate-300 border-t-slate-800 animate-spin" />
      </div>
    );
  }

  return (
    <div className="App">
      {user ? (
        <Dashboard user={user} onLogout={() => setUser(null)} />
      ) : (
        <LoginPage onLoginSuccess={(u) => setUser(u)} />
      )}
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <MainApp />
    </ErrorBoundary>
  );
}

export default App;
